import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Select, Spin, Tooltip, Badge, Empty, Switch, message, DatePicker
} from "antd";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import CrmLeadsServices from "../../../Services/CrmLeadsServices";
import CreateCrmLead from "./CreateCrmLead";
import moment from "moment";
import { FiRefreshCw, FiClock, FiFilter, FiBell, FiExternalLink } from "react-icons/fi";
import { FaExternalLinkAlt, FaBellSlash, FaRocket, FaPhoneAlt, FaCheckCircle, FaFileInvoiceDollar, FaTrophy, FaChartBar, FaPen, FaUser } from "react-icons/fa";
import { ChevronDown } from "lucide-react";
import DataParser from "../../../utlits/Dataparser";
import LeadPipelineService from "../../../Services/LeadPipelineService";
import SocketService from "../../../Services/SocketService";
import UserProfile from "../../Reuseable/UserProfile..jsx";

// Module-level cache: avoids re-fetching identical metadata on every ActionQueue mount
const _aqMetaCache = {};

const { Option } = Select;

// ── Configuration ──────────────────────────────────────────────────────────
const STAGE_CONFIG = {
  new:       { label: "New",       color: "#00BFA5", bg: "#E0F7F4", icon: <img src="/assets/images/new.svg" alt="" style={{ width: "14px", height: "14px" }} /> },
  contacted: { label: "Contacted", color: "#6366F1", bg: "#EEF2FF", icon: <img src="/assets/images/contacted.svg" alt="" style={{ width: "14px", height: "14px" }} /> },
  qualified: { label: "Qualified", color: "#1E293B", bg: "#F1F5F9", icon: <img src="/assets/images/qualified.svg" alt="" style={{ width: "14px", height: "14px" }} /> },
  proposal:  { label: "Proposal",  color: "#F59E0B", bg: "#FFFBEB", icon: <img src="/assets/images/proposal.svg" alt="" style={{ width: "14px", height: "14px" }} /> },
  won:       { label: "Won",       color: "#22C55E", bg: "#F0FDF4", icon: <img src="/assets/images/won.svg" alt="" style={{ width: "14px", height: "14px" }} /> },
  lost:      { label: "Lost",      color: "#EF4444", bg: "#FEF2F2", icon: <img src="/assets/images/lost.svg" alt="" style={{ width: "14px", height: "14px" }} /> },
};

const PRIORITY_CONFIG = {
  1000: { label: "Inbound SLA Breach", color: "#ef4444", bg: "#fee2e2", short: "Critical" },
  800:  { label: "Outbound Overdue",   color: "#f97316", bg: "#ffedd5", short: "High" },
  500:  { label: "SLA Approaching",    color: "#f59e0b", bg: "#fef3c7", short: "Urgent" },
  200:  { label: "Due Today",          color: "#3b82f6", bg: "#dbeafe", short: "Due Today" },
  150:  { label: "Proposal Stale",     color: "#8b5cf6", bg: "#f3e8ff", short: "Stale" },
  100:  { label: "Long Inactive",      color: "#6b7280", bg: "#f3f4f6", short: "Inactive" },
  0:    { label: "Normal",             color: "#10b981", bg: "#d1fae5", short: "Normal" },
};

const COL = { 
  name: '1fr', 
  type: '120px', 
  stage: '130px', 
  owner: '150px', 
  date: '160px', 
  activity: '120px', 
  actions: '100px' 
};

const headSt = { fontSize: '13px', fontWeight: '600', color: '#212529', textAlign: 'left', display: 'block' };

// ── Helpers ──────────────────────────────────────────────────────────────────
function getPriorityConfig(score) {
  const buckets = [1000, 800, 500, 200, 150, 100];
  const match = buckets.find(b => score >= b);
  return PRIORITY_CONFIG[match ?? 0];
}

function formatTimeAgo(dateStr) {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Component ────────────────────────────────────────────────────────────────
const ActionQueue = () => {
  const companyId = DataParser.CompanyId();
  const currentUserId = DataParser.UserId();
  const userName = DataParser.CompanyUserName() || "Junaid";

  const [items, setItems]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [stageFilter, setStageFilter]       = useState([]);
  const [typeFilter, setTypeFilter]         = useState([]);
  const [sourceFilter, setSourceFilter]     = useState([]);
  const [ownerFilter, setOwnerFilter]       = useState([]);
  const [myLeadsOnly, setMyLeadsOnly]       = useState(false);
  const [hideSnoozed, setHideSnoozed]       = useState(true);
  const [activeChip, setActiveChip]         = useState('all');

  const [dateRange, setDateRange]   = useState(null);
  const [ownerFilterAQ, setOwnerFilterAQ] = useState([]);

  const [sources, setSources]     = useState([]);
  const [users, setUsers]         = useState([]);
  const [snoozeLoadingId, setSnoozeLoadingId] = useState(null);
  const [openLeadId, setOpenLeadId] = useState(null);

  const fetchQueue = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await LeadPipelineService.getActionQueue(companyId, hideSnoozed);
      if (res && Array.isArray(res.data)) {
        setItems(res.data);
      } else if (Array.isArray(res)) {
        setItems(res);
      } else {
        setItems([]);
      }
    } catch {
      if (!silent) message.error("Failed to load Action Queue.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, hideSnoozed]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  useEffect(() => {
    if (_aqMetaCache[companyId]) {
      setSources(_aqMetaCache[companyId].sources);
      setUsers(_aqMetaCache[companyId].users);
      return;
    }
    CrmLeadsServices.getInitialData(companyId).then(res => {
      if (res?.Good) {
        const cached = { sources: res.data?.sources || [], users: res.data?.users || [] };
        _aqMetaCache[companyId] = cached;
        setSources(cached.sources);
        setUsers(cached.users);
      }
    }).catch(() => {});
  }, [companyId]);

  const filtered = useMemo(() => {
    const uid = Number(currentUserId);
    // Pre-compute date bounds once (not per item)
    const dateStart = (dateRange?.[0] && dateRange?.[1]) ? dateRange[0].clone().startOf('day') : null;
    const dateEnd = (dateRange?.[0] && dateRange?.[1]) ? dateRange[1].clone().endOf('day') : null;

    // Single-pass filter — replaces 8 sequential .filter() chains (O(8N) → O(N))
    return items.filter(i => {
      const lead = i.lead || i;
      const createdBy = typeof lead.created_by === 'object' ? (lead.created_by?.id || lead.created_by?._id) : lead.created_by;
      const owner = typeof lead.lead_owner === 'object' ? (lead.lead_owner?.id || lead.lead_owner?._id) : lead.lead_owner;
      const stage = i.lead?.stage || i.stage;

      // Chip filter
      if (activeChip === 'created_by_me' && Number(createdBy) !== uid) return false;
      if (activeChip === 'assigned_to_me' && Number(owner) !== uid) return false;
      if (activeChip !== 'all' && activeChip !== 'created_by_me' && activeChip !== 'assigned_to_me' && stage !== activeChip) return false;

      // My leads toggle
      if (myLeadsOnly && Number(lead.lead_owner) !== uid && Number(lead.assigned_to) !== uid) return false;

      // Dropdown filters
      if (stageFilter.length > 0 && !stageFilter.some(s => stage === s)) return false;
      if (typeFilter.length > 0 && !typeFilter.some(t => (i.lead?.workflow_type || i.workflow_type) === t)) return false;
      if (sourceFilter.length > 0 && !sourceFilter.some(s => String(lead.lead_source) === String(s))) return false;
      if (ownerFilter.length > 0 && !ownerFilter.some(o => String(lead.lead_owner) === String(o))) return false;
      if (ownerFilterAQ.length > 0 && !ownerFilterAQ.some(o => String(lead.lead_owner) === String(o))) return false;

      // Date filter — pre-computed bounds
      if (dateStart && dateEnd) {
        const d = lead.due_date;
        if (!d) return false;
        const leadDate = moment(d).startOf('day');
        if (!leadDate.isValid() || leadDate.isBefore(dateStart) || leadDate.isAfter(dateEnd)) return false;
      }

      return true;
    });
  }, [items, myLeadsOnly, stageFilter, typeFilter, sourceFilter, ownerFilter, ownerFilterAQ, dateRange, currentUserId, activeChip]);

  const counts = useMemo(() => {
    const uid = Number(currentUserId);
    const map = { new: 0, contacted: 0, qualified: 0, proposal: 0, won: 0, lost: 0, created_by_me: 0, assigned_to_me: 0 };
    items.forEach(i => {
      const lead = i.lead || i;
      const st = (lead.stage || 'new').toLowerCase();
      if (map.hasOwnProperty(st)) map[st]++;
      const createdBy = typeof lead.created_by === 'object' ? (lead.created_by?.id || lead.created_by?._id) : lead.created_by;
      const owner = typeof lead.lead_owner === 'object' ? (lead.lead_owner?.id || lead.lead_owner?._id) : lead.lead_owner;
      if (Number(createdBy) === uid) map.created_by_me++;
      if (Number(owner) === uid) map.assigned_to_me++;
    });
    return map;
  }, [items, currentUserId]);

  const SUMMARY_CARDS = [
    { key: 'new',            label: 'New',            icon: '/assets/images/new.svg',       count: counts.new },
    { key: 'contacted',      label: 'Contacted',      icon: '/assets/images/contacted.svg', count: counts.contacted },
    { key: 'qualified',      label: 'Qualified',      icon: '/assets/images/qualified.svg', count: counts.qualified },
    { key: 'proposal',       label: 'Proposal',       icon: '/assets/images/proposal.svg',  count: counts.proposal },
    { key: 'won',            label: 'Won',            icon: '/assets/images/won.svg',       count: counts.won },
    { key: 'lost',           label: 'Lost',           icon: '/assets/images/lost.svg',      count: counts.lost },
    { key: 'created_by_me',  label: 'Created by me',  IconComponent: FaPen,  count: counts.created_by_me },
    { key: 'assigned_to_me', label: 'Assigned to me', IconComponent: FaUser, count: counts.assigned_to_me },
  ];

  useEffect(() => {
    const handleLeadUpdate = (data) => {
      if (String(data?.company_id) !== String(companyId)) return;
      fetchQueue(true);
    };
    SocketService.connect();
    SocketService.on("lead_update", handleLeadUpdate);
    return () => SocketService.off("lead_update", handleLeadUpdate);
  }, [fetchQueue, companyId]);

  const handleSnooze = async (e, item) => {
    e.stopPropagation();
    const leadId = item.id || item.lead?.id;
    setSnoozeLoadingId(leadId);
    try {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      await LeadPipelineService.snoozeLead(leadId, {
        company_id:    companyId,
        snooze_until:  tomorrow.toISOString(),
      });
      message.success("Lead snoozed until tomorrow.");
      fetchQueue();
    } catch (err) {
      message.error(err?.response?.data?.data || err?.response?.data?.message || "Failed to snooze lead.");
    } finally {
      setSnoozeLoadingId(null);
    }
  };

  const handleFilterChange = (setter) => (newValues) => setter(newValues || []);

  const handleRefresh = () => {
    setMyLeadsOnly(false);
    setStageFilter([]);
    setTypeFilter([]);
    setSourceFilter([]);
    setOwnerFilter([]);
    setHideSnoozed(true);
    setActiveChip('all');
    fetchQueue();
  };

  const goToLead = (e, leadId) => {
    if (e) e.stopPropagation();
    setOpenLeadId(leadId);
  };

  return (
    <div className="main-content">
      <div className="page-content" style={{ minHeight: "100vh", backgroundColor: "#fff" }}>
        <div className="container-fluid" style={{ padding: "15px" }}>
          
          <style>{`
            .task-stat-chip:hover { border-color: #0DDFAB !important; }
            .lead-header {
                display: flex; align-items: center; padding: 10px 0;
                background: #F2F2F2; border-radius: 0; border-bottom: 1px solid #e5e7eb;
            }
            .lead-row {
                display: flex; width: 100%; align-items: center;
                min-height: 48px; border-bottom: 1px solid #f3f4f6; padding: 4px 0;
                transition: background-color 0.15s; background: #fff;
            }
            .lead-row:hover { background-color: #f9fafb !important; }
            .priority-indicator {
                flex-shrink: 0;
                align-self: stretch;
                width: 4px;
                transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                overflow: hidden;
                white-space: nowrap;
                cursor: default;
                border-radius: 0 4px 4px 0;
                display: flex;
                align-items: center;
            }
            .priority-indicator:hover {
                width: 90px;
                box-shadow: 2px 0 8px rgba(0,0,0,0.1);
            }
            .priority-indicator span {
                opacity: 0;
                transition: opacity 0.2s ease;
                white-space: nowrap;
                font-size: 10px;
                font-weight: 700;
                color: #fff;
                padding-left: 6px;
            }
            .priority-indicator:hover span { opacity: 1; transition-delay: 0.1s; }


            /* FILTER STYLING — matches Tasks.jsx pattern */
            .aq-filters .float-select-wrap.custom-filter { position: relative; }
            .aq-filters .float-select-wrap.custom-filter .ant-select-selector {
                height: 34px !important; min-height: 34px !important; border-radius: 6px !important;
                border: 1.5px solid #F2F2F2 !important; display: flex !important; align-items: center !important;
            }
            .aq-filters .float-select-wrap.custom-filter .ant-select-selector:hover,
            .aq-filters .float-select-wrap.custom-filter .ant-select-focused .ant-select-selector { border-color: #1D1D1D !important; box-shadow: none !important; }
            .aq-filters .float-select-wrap.custom-filter .ant-select-clear { display: none !important; }
            .aq-filters .float-select-wrap.custom-filter .ant-select-selection-overflow {
                display: flex !important; align-items: center !important;
                flex-wrap: nowrap !important; height: 100% !important; overflow: hidden !important;
            }
            .aq-filters .float-select-wrap.custom-filter .ant-select-selection-overflow-item { display: flex !important; align-items: center !important; }
            .aq-filters .float-select-wrap.custom-filter .ant-select-selection-item,
            .aq-filters .float-select-wrap.custom-filter .ant-select-selection-placeholder { line-height: 30px !important; font-size: 11px !important; font-weight: 300 !important; }
            .aq-filters .float-select-wrap.custom-filter label {
                position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
                font-size: 11px !important; font-weight: 300 !important; color: #D2D2D2; background: transparent;
                padding: 0 4px; z-index: 10; transition: all 0.2s ease; pointer-events: none; white-space: nowrap;
            }
            .aq-filters .float-select-wrap.custom-filter.has-value label,
            .aq-filters .float-select-wrap.custom-filter:focus-within label {
                top: 0; transform: translateY(-50%); font-size: 11px !important;
                font-weight: 600 !important; color: #1D1D1D; background: #fff;
            }
            .aq-filters .float-select-wrap.custom-filter:hover label { color: #1D1D1D !important; }
            .aq-filters .float-select-wrap.custom-filter:hover > svg,
            .aq-filters .float-select-wrap.custom-filter:focus-within > svg { color: #1D1D1D !important; }
            .aq-filters .float-select-wrap.custom-filter .ant-picker {
                height: 34px !important; min-height: 34px !important; border-radius: 6px !important;
                border: 1.5px solid #F2F2F2 !important; font-size: 11px !important; padding: 0 10px !important;
            }
            .aq-filters .float-select-wrap.custom-filter .ant-picker:hover,
            .aq-filters .float-select-wrap.custom-filter .ant-picker-focused { border-color: #1D1D1D !important; box-shadow: none !important; }
            .aq-filters .task-date-range-wrap { position: relative; }
            .aq-filters .task-date-range-wrap::before,
            .aq-filters .task-date-range-wrap::after {
                position: absolute; top: 0; transform: translateY(-50%);
                font-size: 11px !important; font-weight: 600 !important; color: #D2D2D2;
                background: #fff; padding: 0 3px; z-index: 10;
                pointer-events: none; opacity: 0; transition: color 0.2s, opacity 0.2s;
            }
            .aq-filters .task-date-range-wrap::before { content: 'Start Date'; left: 12px; }
            .aq-filters .task-date-range-wrap::after  { content: 'End Date';   left: calc(50% + 8px); }
            .aq-filters .task-date-range-wrap.has-value::before { opacity: 1; color: #1D1D1D !important; }
            .aq-filters .task-date-range-wrap.has-end-value::after  { opacity: 1; color: #1D1D1D !important; }
            .aq-filters .task-date-range-wrap:hover .ant-picker { border-color: #1D1D1D !important; box-shadow: none !important; }
            .aq-filters .task-date-range-wrap:hover .ant-picker-input > input { color: #1D1D1D !important; }
            .aq-filters .task-date-range-wrap:hover .ant-picker-input > input::placeholder { color: #1D1D1D !important; opacity: 1 !important; }
            .aq-filters .float-select-wrap.custom-filter .ant-picker-input > input { font-size: 11px !important; font-weight: 300 !important; }
            .aq-filters .float-select-wrap.custom-filter .ant-picker-separator { font-size: 11px !important; }
            .aq-filters .float-select-wrap.custom-filter .ant-picker-suffix { font-size: 13px !important; }

            @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            .spin-icon { animation: spin 1s linear infinite; }

            .summary-card {
                background: #fff; border: 1.5px solid #e9ecef; border-radius: 10px;
                padding: 6px 10px; display: flex; flex-direction: column; 
                align-items: flex-start; transition: all 0.2s;
                cursor: pointer; position: relative; min-width: 0;
                box-shadow: 0 1px 3px rgba(0,0,0,0.04);
            }
            .summary-card:hover { border-color: #0DDFAB !important; }
            .summary-card.active { border-color: #0DDFAB; background: #fff; }
            .card-icon-box {
                width: 26px; height: 26px; border-radius: 6px; background: #d1faf0;
                display: flex; align-items: center; justify-content: center; color: #0a9e7a;
                font-size: 14px; margin-bottom: 2px; flex-shrink: 0;
            }
            .card-label { font-size: 11px; font-weight: 500; color: #64748b; margin-bottom: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
            .card-count { font-size: 18px; font-weight: 700; color: #1a2332; margin-bottom: 0; line-height: 1; flex-shrink: 0; }
          `}</style>

          {/* Status Cards */}
          <div className="mb-6" style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '10px' }}>
            {SUMMARY_CARDS.map(card => {
              const isActive = activeChip === card.key;
              return (
                <div 
                  key={card.key} 
                  className={`summary-card ${isActive ? 'active' : ''}`}
                  onClick={() => setActiveChip(isActive ? 'all' : card.key)}
                >
                  <div className="card-icon-box">
                    {card.IconComponent ? (
                      <card.IconComponent size={14} />
                    ) : (
                      <img
                        src={card.icon}
                        alt=""
                        style={{
                          width: "15px",
                          height: "15px",
                          filter: "brightness(0) saturate(100%) invert(43%) sepia(93%) saturate(394%) hue-rotate(118deg) brightness(91%) contrast(95%)"
                        }}
                      />
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', width: '100%', gap: '4px' }}>
                    <p className="card-label">{card.label}</p>
                    <p className="card-count">{String(card.count).padStart(2, '0')}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Title and Secondary Filter Header */}
          <div className="d-flex align-items-end justify-content-between mb-3 px-1 mt-3">
            <div>
              <h4 style={{ margin: 0, fontWeight: 700, color: "#1e293b", fontSize: '20px' }}>Action Queue</h4>
              <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: '13px' }}>
                {filtered.length} leads requiring attention sorted by priority
              </p>
            </div>
            
            <div className="aq-filters" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div className={`float-select-wrap custom-filter ${stageFilter.length > 0 ? 'has-value' : ''}`} style={{ width: '140px', position: 'relative' }}>
                <label>Stage</label>
                <Select
                  mode="multiple"
                  placeholder=""
                  style={{ width: '100%' }}
                  value={stageFilter}
                  onChange={handleFilterChange(setStageFilter)}
                  allowClear
                  maxTagCount={0}
                  maxTagPlaceholder={() => stageFilter.length > 0 ? `${stageFilter.length} Selected` : ""}
                >
                  {Object.entries(STAGE_CONFIG).map(([key, cfg]) => (
                    <Option key={key} value={key}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.color, display: 'inline-block' }} />
                        {cfg.label}
                      </span>
                    </Option>
                  ))}
                </Select>
                <ChevronDown size={14} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#D2D2D2', pointerEvents: 'none', zIndex: 5 }} />
              </div>

              <div className={`float-select-wrap custom-filter ${typeFilter.length > 0 ? 'has-value' : ''}`} style={{ width: '140px', position: 'relative' }}>
                <label>Lead Interest</label>
                <Select
                  mode="multiple"
                  placeholder=""
                  style={{ width: '100%' }}
                  value={typeFilter}
                  onChange={handleFilterChange(setTypeFilter)}
                  allowClear
                  maxTagCount={0}
                  maxTagPlaceholder={() => typeFilter.length > 0 ? `${typeFilter.length} Selected` : ""}
                >
                  <Option value="inbound">Inbound</Option>
                  <Option value="outbound">Outbound</Option>
                </Select>
                <ChevronDown size={14} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#D2D2D2', pointerEvents: 'none', zIndex: 5 }} />
              </div>

              <div className={`float-select-wrap custom-filter ${ownerFilterAQ.length > 0 ? 'has-value' : ''}`} style={{ width: '150px', position: 'relative' }}>
                <label>Lead Owner</label>
                <Select
                  mode="multiple"
                  placeholder=""
                  style={{ width: '100%' }}
                  value={ownerFilterAQ}
                  onChange={(v) => setOwnerFilterAQ(v)}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                  maxTagCount={0}
                  maxTagPlaceholder={(v) => v.length > 0 ? `${v.length} Selected` : ""}
                >
                  {users.map(u => (
                    <Option key={u.id} value={String(u.id)}>{u.first_name} {u.last_name}</Option>
                  ))}
                </Select>
                <ChevronDown size={14} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#D2D2D2', pointerEvents: 'none', zIndex: 5 }} />
              </div>

              <div className={`float-select-wrap custom-filter task-date-range-wrap ${dateRange?.[0] ? 'has-value' : ''} ${dateRange?.[1] ? 'has-end-value' : ''}`} style={{ flex: '0 1 270px' }}>
                <DatePicker.RangePicker
                  className="w-100"
                  format="DD-MM-YYYY"
                  allowClear
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates)}
                  placeholder={['Start Date', 'End Date']}
                  separator="→"
                  suffixIcon={
                    <div style={{ backgroundColor: '#F4F4F4', borderRadius: '5px', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '-4px' }}>
                      <i className="uil-schedule" style={{ color: '#888', fontSize: '14px' }} />
                    </div>
                  }
                />
              </div>
            </div>
          </div>

          {/* Priority Legend */}
          {(() => {
            const LEGEND = [
              { score: 1000, hint: 'Inbound >24h' },
              { score: 800,  hint: 'Outbound overdue' },
              { score: 500,  hint: 'Inbound 2–24h' },
              { score: 200,  hint: 'Due today' },
              { score: 150,  hint: 'Proposal >7d idle' },
              { score: 100,  hint: 'No activity >14d' },
              { score: 0,    hint: 'On track' },
            ];
            return (
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '5px', marginBottom: '10px', padding: '6px 10px' }}>
                <span style={{ fontSize: '10px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: '4px', flexShrink: 0 }}>SLA</span>
                {LEGEND.map(({ score, hint }) => {
                  const cfg = PRIORITY_CONFIG[score];
                  return (
                    <div key={score} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 8px 2px 5px', borderRadius: '20px', background: cfg.bg }}>
                      <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
                      <span style={{ fontSize: '10px', fontWeight: 700, color: cfg.color, whiteSpace: 'nowrap' }}>{cfg.short}</span>
                      <span style={{ fontSize: '9px', color: cfg.color, opacity: 0.75, whiteSpace: 'nowrap', marginLeft: '1px' }}>· {hint}</span>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Table Listing */}
          <div className="w-100" style={{ minHeight: '400px' }}>
            <div className="lead-header">
                <div style={{ flex: 1, paddingLeft: '14px' }}><span style={headSt}>Task / Lead Name</span></div>
                <div style={{ width: COL.type, padding: '0 8px' }}><span style={headSt}>Type</span></div>
                <div style={{ width: COL.stage, padding: '0 8px' }}><span style={headSt}>Stage</span></div>
                <div style={{ width: COL.owner, padding: '0 8px' }}><span style={headSt}>Owner</span></div>
                <div style={{ width: COL.date, padding: '0 8px' }}><span style={headSt}>Follow Up Date</span></div>
                <div style={{ width: COL.activity, padding: '0 8px' }}><span style={headSt}>Last Activity</span></div>
                <div style={{ width: COL.actions, padding: '0 8px', textAlign: 'center' }}><span style={headSt}>Actions</span></div>
            </div>

            {loading ? (
                [1, 2, 3, 4, 5].map(i => (
                    <div key={i} className="lead-row">
                        <div style={{ flex: 1, paddingLeft: '20px' }}><Skeleton width="80%" /></div>
                        <div style={{ width: COL.type, padding: '0 8px' }}><Skeleton width={60} /></div>
                        <div style={{ width: COL.stage, padding: '0 8px' }}><Skeleton width={80} /></div>
                        <div style={{ width: COL.owner, padding: '0 8px' }}><Skeleton circle width={24} height={24} /></div>
                        <div style={{ width: COL.date, padding: '0 8px' }}><Skeleton width={80} /></div>
                        <div style={{ width: COL.activity, padding: '0 8px' }}><Skeleton width={60} /></div>
                        <div style={{ width: COL.actions, padding: '0 8px' }}><Skeleton width={40} /></div>
                    </div>
                ))
            ) : filtered.length === 0 ? (
                <div className="py-5 text-center bg-white" style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <Empty description="No leads in the queue — great work!" />
                </div>
            ) : (
                filtered.map((item, idx) => {
                    const lead = item.lead || item;
                    const score = item.priority_score ?? 0;
                    const pCfg = getPriorityConfig(score);
                    const stageCfg = STAGE_CONFIG[lead.stage] || STAGE_CONFIG.new;
                    const followUp = (lead.due_date || lead.next_action_at) 
                        ? moment(lead.due_date || lead.next_action_at).format("DD, MMMM YYYY") 
                        : "—";

                    return (
                        <div className="lead-row" key={lead.id}>
                            <div className="priority-indicator" style={{ backgroundColor: pCfg.color }}>
                                <span>{pCfg.short} · {score}</span>
                            </div>
                            
                            {/* Lead Name */}
                            <div style={{ flex: 1, paddingLeft: '10px', paddingRight: '10px', overflow: 'hidden' }}>
                                <span style={{ color: '#111827', fontSize: '13px', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                                    {lead.title || `Lead #${lead.id}`}
                                </span>
                            </div>

                            {/* Type */}
                            <div style={{ width: COL.type, padding: '0 8px' }}>
                                {lead.workflow_type && (
                                    <div style={{ 
                                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                                        padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                                        background: lead.workflow_type === "inbound" ? "#F0F8FF" : "#F0FFF8",
                                        color: lead.workflow_type === "inbound" ? "#7394B1" : "#4B936F"
                                    }}>
                                        <i className={lead.workflow_type === "inbound" ? "uil uil-arrow-down-left" : "uil uil-arrow-up-right"} />
                                        {lead.workflow_type.charAt(0).toUpperCase() + lead.workflow_type.slice(1)}
                                    </div>
                                )}
                            </div>

                            {/* Stage */}
                            <div style={{ width: COL.stage, padding: '0 8px' }}>
                                <div style={{ 
                                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                                    padding: '3px 10px', borderRadius: '5px', fontSize: '11px', fontWeight: '700',
                                    background: stageCfg.bg, color: stageCfg.color
                                }}>
                                    {stageCfg.icon}
                                    {stageCfg.label}
                                </div>
                            </div>

                            {/* Owner */}
                            <div style={{ width: COL.owner, padding: '0 8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <UserProfile 
                                        username={item.owner ? `${item.owner.first_name || ""} ${item.owner.last_name || ""}`.trim() : "—"} 
                                        userImage={item.owner?.image || item.owner?.profile_picture}
                                        style={{ width: '24px', height: '24px' }}
                                    />
                                    <span style={{ fontSize: '13px', color: '#1d1d1d', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {item.owner ? item.owner.first_name : "—"}
                                    </span>
                                </div>
                            </div>

                            {/* FollowUp */}
                            <div style={{ width: COL.date, padding: '0 8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#111827', fontSize: '13px' }}>
                                    <i className="uil uil-calendar-alt" style={{ color: '#0DDFAB', fontSize: '20px' }} />
                                    <span style={{ fontWeight: '500' }}>{followUp}</span>
                                </div>
                            </div>

                            {/* Activity */}
                            <div style={{ width: COL.activity, padding: '0 8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#111827', fontSize: '13px' }}>
                                    <i className="uil uil-history" style={{ color: '#0DDFAB', fontSize: '20px' }} />
                                    <span style={{ fontWeight: '500' }}>{formatTimeAgo(lead.last_activity_at)}</span>
                                </div>
                            </div>

                            {/* Actions */}
                            <div style={{ width: COL.actions, padding: '0 8px', display: 'flex', gap: '8px', justifyContent: 'flex-start' }}>
                                <Tooltip title="Snooze until tomorrow">
                                    <button 
                                      onClick={(e) => handleSnooze(e, item)}
                                      disabled={snoozeLoadingId === (item.id || item.lead?.id)}
                                      className="btn p-0" 
                                      style={{ color: '#111827' }}
                                    >
                                        {snoozeLoadingId === (item.id || item.lead?.id) ? <Spin size="small" /> : <FiBell size={18} />}
                                    </button>
                                </Tooltip>
                                <Tooltip title="View in Leads">
                                    <button onClick={(e) => goToLead(e, lead.id)} className="btn p-0" style={{ color: '#111827' }}>
                                        <FiExternalLink size={18} />
                                    </button>
                                </Tooltip>
                            </div>
                        </div>
                    );
                })
            )}
          </div>
        </div>
      </div>

      {/* Lead detail modal — opens inline without leaving Action Queue */}
      {openLeadId && (
        <div
          style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100005 }}
          onClick={() => setOpenLeadId(null)}
        >
          <CreateCrmLead
            editId={openLeadId}
            canEditLead={true}
            onCancel={() => setOpenLeadId(null)}
            onSuccess={() => { setOpenLeadId(null); fetchQueue(true); }}
          />
        </div>
      )}
    </div>
  );
};

export default ActionQueue;
